import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl } from "@angular/forms";
import { Location } from "@app-cmc/models";
import { CashRegisterService, CashRegistersService, CompanyDataService, LocationService } from "@app-cmc/services";
import { Subject } from "rxjs";
import { IFilterSelectItem } from "ui-kit";
import { filter, take, takeUntil } from "rxjs/operators";
import { TranslocoService } from "@ngneat/transloco";
import { CashRegister } from "cfs-communication-pack";

@Component({
  selector: "app-header-content",
  templateUrl: "./header-content.component.html",
  styleUrls: ["./header-content.component.scss"]
})
export class HeaderContentComponent implements OnInit, OnDestroy {
  protected location = new Location();
  protected cashRegister: CashRegister | null = null;
  protected locationControl = new FormControl<number>(null);
  protected locationsList: IFilterSelectItem<number>[] = [];
  protected cashRegisterControl = new FormControl<number>(null);
  protected cashRegistersList: IFilterSelectItem<number>[] = [];

  private unsubscribe$ = new Subject<void>();
  private locations: Location[] = [];
  private cashRegisters: CashRegister[] = [];

  constructor(
    public locationService: LocationService,
    public cashRegisterService: CashRegisterService,
    private companyDataService: CompanyDataService,
    private cashRegistersService: CashRegistersService,
    private translateService: TranslocoService
  ) {}

  ngOnInit() {
    this.locationControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((id: number) => {
      this.setLocations(id);
    });

    this.cashRegisterControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((id: number) => {
      this.setCashRegisters(id);
    });

    this.locationService
      .getAll()
      .pipe(take(1))
      .subscribe((res) => {
        if (res && res.length) {
          this.locations = res;
          this.locationsList = this.locations?.map((item: Location) => ({
            value: +item.id,
            title: this.getInfoLocation(item)
          }));
          const locationId = this.locationService.getSelectedLocationLocalStorage();

          if (locationId && this.locations.some((f) => f.id === locationId)) {
            this.location = this.locations.find((f) => f.id === locationId);
            this.reloadCashRegisters();
          } else {
            this.location = res[0];
            this.locationControl.setValue(this.location?.id);
          }
        }
      });

    this.cashRegistersService.cashRegisters$
      .pipe(
        filter((list: CashRegister[]) => list.length > 0),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((cashRegisters: CashRegister[]) => {
        this.cashRegisters = cashRegisters;
        this.cashRegistersList = this.cashRegisters?.map((item: CashRegister) => ({
          value: +item.id,
          title: this.getInfoCashRegister(item)
        }));
        const cashRegisterId: number = this.cashRegisterService.getSelectedCashRegisterIdFromStorage();
        const currentCashRegister: CashRegister | null = cashRegisterId ? this.cashRegisters.find((item: CashRegister) => item.id === cashRegisterId) : null;
        this.cashRegister = currentCashRegister || cashRegisters[0];

        this.cashRegisterControl.setValue(this.cashRegister?.id);
      });
  }

  private setLocations(id: number) {
    this.location = this.locations?.find((item: Location) => item.id === id);

    this.locationService.setSelectedLocationLocalStorage(this.location?.id);
    this.companyDataService.retrieveData();
    this.reloadCashRegisters();
  }

  private setCashRegisters(id: number): void {
    this.cashRegister = this.cashRegisters?.find((item: CashRegister) => item.id === id);

    this.cashRegisterService.setSelectedCashRegisterIdToStorage(this.cashRegister?.id);
    this.cashRegistersService.updateCurrentCashRegister(this.cashRegister);
  }

  private reloadCashRegisters(): void {
    this.cashRegistersService.update(this.location.id);
  }

  private getInfoLocation(loc: Location): string {
    return `${loc.companyName}, ${loc.address}, ${loc.city}, ${loc.state}, ${loc.zip}`;
  }

  private getInfoCashRegister(cashRegister: CashRegister): string {
    let result = cashRegister.name;

    if (cashRegister.cfsCode && cashRegister.cfsCode !== "") {
      const translatedText = this.translateService.translateObject("cfs.cashregisters_dropdown.cfs_code", { code: cashRegister.cfsCode });
      result = `${result}; ${translatedText}`;
    }

    if (cashRegister.terminalId && cashRegister.terminalId !== "") {
      const translatedText = this.translateService.translateObject("cfs.cashregisters_dropdown.terminal_id", {
        terminalId: cashRegister.terminalId
      });
      result = `${result}; ${translatedText}`;
    }

    return result;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
